import { addSeconds, format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useTopUpCoversUsage } from 'features/credits/hooks/use-credits';
import { SubscriptionUsageMeter } from 'features/subscription/services/subscription-service';
import { UsageBar } from './usage-bar';
import { UsageCreditCard } from './usage-credit-card';
import { formatCreditValue } from './usage-credits';

/** This billing period's AI credits: what is left, what was used, and when it resets. */
export function UsageMonthCard({ meter }: { meter: SubscriptionUsageMeter }) {
  const { t } = useTranslation();
  const topUpCovers = useTopUpCoversUsage();
  const percent = meter.included > 0 ? (meter.used / meter.included) * 100 : 0;
  // A period ending at 23:59:59 renews the next day; one ending at midnight renews that day.
  const renewsOn = format(addSeconds(new Date(meter.periodEndUtc), 1), 'MMM d, yyyy');

  return (
    <UsageCreditCard
      title={t('USAGE_MONTHLY_CREDITS', { defaultValue: 'Monthly credits' })}
      value={formatCreditValue(meter.remaining, meter.quantityScale)}
      unit={t('USAGE_UNIT_LEFT', { unit: meter.unitLabel, defaultValue: '{{unit}} left' })}
      footer={t('USAGE_RENEWS_ON', { date: renewsOn, defaultValue: 'Renews on {{date}}' })}
    >
      <UsageBar percent={percent} warn={meter.overage > 0} calm={topUpCovers && percent >= 100} />
      <p className="text-xs text-muted-foreground">
        {t('CREDITS_USED_OF_INCLUDED', {
          used: formatCreditValue(meter.used, meter.quantityScale),
          included: formatCreditValue(meter.included, meter.quantityScale),
          unit: meter.unitLabel,
          defaultValue: '{{used}} of {{included}} {{unit}} used',
        })}
      </p>
    </UsageCreditCard>
  );
}
